import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');
export const updateMe = (data) => api.put('/auth/me', data);

// Users
export const getUsers = () => api.get('/users');
export const getUserNames = () => api.get('/users/names');
export const getUser = (id) => api.get(`/users/${id}/stats`);
export const inviteUser = (data) => api.post('/users/invite', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Clients
export const getClients = (params) => api.get('/clients', { params });
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id, permanent = false) => api.delete(`/clients/${id}`, { params: { permanent } });

// Projects
export const getProjects = (params) => api.get('/projects', { params });
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id, permanent = false) => api.delete(`/projects/${id}`, { params: { permanent } });
export const duplicateProject = (id) => api.post(`/projects/${id}/duplicate`);

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id, permanent = false) => api.delete(`/tasks/${id}`, { params: { permanent } });
export const moveTask = (id, data) => api.post(`/tasks/${id}/move`, data);

// Time Entries
export const getTimeEntries = (params) => api.get('/time-entries', { params });
export const createTimeEntry = (data) => api.post('/time-entries', data);
export const updateTimeEntry = (id, data) => api.put(`/time-entries/${id}`, data);
export const deleteTimeEntry = (id) => api.delete(`/time-entries/${id}`);

// Active Timer (Server-side)
export const getActiveTimer = () => api.get('/timer/active');
export const startTimer = (data) => api.post('/timer/start', data);
export const stopTimer = () => api.post('/timer/stop');
export const cancelTimer = () => api.delete('/timer/cancel');

// Revenue
export const getRevenue = (params) => api.get('/revenue', { params });
export const createRevenue = (data) => api.post('/revenue', data);
export const updateRevenue = (id, data) => api.put(`/revenue/${id}`, data);
export const deleteRevenue = (id) => api.delete(`/revenue/${id}`);

// Analytics
export const getTimeByUser = (projectId) => api.get('/analytics/time-by-user', { params: { project_id: projectId } });
export const getTimeByProject = (clientId) => api.get('/analytics/time-by-project', { params: { client_id: clientId } });
export const getRevenueSummary = (year) => api.get('/analytics/revenue-summary', { params: { year } });
export const getPipelineSummary = () => api.get('/analytics/pipeline-summary');
export const getFinancialSummary = (params) => api.get('/analytics/financial-summary', { params });
export const getClientFinancialSummary = (clientId) => api.get(`/analytics/client-financial-summary/${clientId}`);
export const getClientMonthlyFinancials = (clientId, months = 24) => api.get(`/clients/${clientId}/financials`, { params: { months } });
export const getCapacityWarnings = () => api.get('/analytics/capacity-warnings');
export const getNonBillableSummary = (params) => api.get('/analytics/non-billable-summary', { params });

// Audit Log
export const getAuditLogs = (params) => api.get('/audit-logs', { params });

// Admin - Data Migration
export const runDataMigration = () => api.post('/admin/migrate-financial-data');

// Notes
export const getNotes = (params) => api.get('/notes', { params });
export const getNote = (id) => api.get(`/notes/${id}`);
export const createNote = (data) => api.post('/notes', data);
export const updateNote = (id, data) => api.put(`/notes/${id}`, data);
export const deleteNote = (id) => api.delete(`/notes/${id}`);

// Dashboard
export const getUpcomingTasks = () => api.get('/dashboard/upcoming-tasks');
export const getUserMonthlyTime = (userId) => api.get(`/analytics/user-monthly-time/${userId}`);
export const getDashboardAlerts = () => api.get('/dashboard/alerts');
export const getMonthlySummary = (month) => api.get('/analytics/monthly-summary', { params: { month } });
export const getUserCapacity = (month) => api.get('/analytics/user-capacity', { params: { month } });
export const getClientsHealth = () => api.get('/analytics/clients-health');
export const getProjectProfitability = (month) => api.get('/analytics/project-profitability', { params: month ? { month } : {} });
export const getClientHealth = (clientId) => api.get(`/clients/${clientId}/health`);

// External Costs
export const getExternalCosts = (activeOnly = false) => api.get('/external-costs', { params: { active_only: activeOnly } });
export const createExternalCost = (data) => api.post('/external-costs', data);
export const updateExternalCost = (id, data) => api.put(`/external-costs/${id}`, data);
export const deleteExternalCost = (id) => api.delete(`/external-costs/${id}`);

// Data Migration
export const runCapacityMigration = () => api.post('/admin/migrate-capacity-data');

// CRM
export const getCRMContacts = (params) => api.get('/crm/contacts', { params });
export const getCRMContact = (id) => api.get(`/crm/contacts/${id}`);
export const createCRMContact = (data) => api.post('/crm/contacts', data);
export const updateCRMContact = (id, data) => api.put(`/crm/contacts/${id}`, data);
export const deleteCRMContact = (id) => api.delete(`/crm/contacts/${id}`);
export const getContactActivities = (id) => api.get(`/crm/contacts/${id}/activities`);
export const createCRMActivity = (data) => api.post('/crm/activities', data);
export const deleteCRMActivity = (id) => api.delete(`/crm/activities/${id}`);
export const getCRMDeals = (params) => api.get('/crm/deals', { params });
export const createCRMDeal = (data) => api.post('/crm/deals', data);
export const updateCRMDeal = (id, data) => api.put(`/crm/deals/${id}`, data);
export const deleteCRMDeal = (id) => api.delete(`/crm/deals/${id}`);
export const convertDealToProject = (id) => api.post(`/crm/deals/${id}/convert`);

export default api;
